// src/components/blocking/BlockingAccessScreens.tsx
//
// Full-pane blocking screens shown when the backend access gate rejects
// the user on bootstrap. Rendered before the chat UI mounts, so there is
// no nav, no tabs, and no CTA buttons — every resolution path is off-line
// (admin / sales / support) and surfaced as an inline mailto link.
//
// Mapping from gate `X-Subscription-Status` to which screen renders is
// owned by `App.tsx`. This module just defines the two screens and the
// shared layout.

import * as React from "react";
import { PersonAdd24Regular } from "@fluentui/react-icons";

import csLogoIcon from "../../../assets/icon-64.png";
import { SUPPORT_EMAIL } from "../../config/constants";

interface BlockingScreenProps {
  icon: React.ReactNode;
  title: string;
  bodyBeforeLink: string;
  mailto: string;
  bodyAfterLink: string;
}

/**
 * Shared full-pane blocking layout. White background, centered icon + h1
 * + body paragraph with one inline mailto link. No header, no tabs, no
 * CTA buttons — these screens replace the chat UI entirely.
 *
 * Accessibility: uses the implicit `role="main"` of <main> (the landmark
 * semantic is correct — these screens ARE the page's main content), named
 * by the h1 via aria-labelledby. We deliberately do NOT use role="alert"
 * or aria-live here: those are for transient announcements, but a blocking
 * screen is the entire static UI for the session. Screen readers will
 * surface the heading naturally on landmark navigation.
 */
const BlockingScreen: React.FC<BlockingScreenProps> = ({
  icon,
  title,
  bodyBeforeLink,
  mailto,
  bodyAfterLink,
}) => {
  const titleId = React.useId();
  return (
    <main
      aria-labelledby={titleId}
      style={{
        height: "100vh",
        width: "100%",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "60px 28px 28px",
        fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
        textAlign: "center",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 20, color: "#0078D4" }}>{icon}</div>
      <h1
        id={titleId}
        style={{
          fontSize: 22,
          fontWeight: 600,
          lineHeight: 1.3,
          color: "#242424",
          margin: "0 0 12px 0",
          maxWidth: 320,
        }}
      >
        {title}
      </h1>
      <p
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: "#424242",
          margin: 0,
          maxWidth: 320,
        }}
      >
        {bodyBeforeLink}
        <a href={`mailto:${mailto}`} style={{ color: "#0078D4", textDecoration: "none" }}>
          {mailto}
        </a>
        {bodyAfterLink}
      </p>
    </main>
  );
};

/**
 * Shown for `no_org` and `suspended` deny statuses — the org-level
 * resolution path (sales / IT admin). Copy is intentionally generic
 * enough to cover both "never set up" and "currently halted" without
 * leaking which one it is.
 */
export const BlockingNoOrgScreen: React.FC = () => (
  <BlockingScreen
    icon={
      <img
        src={csLogoIcon}
        alt=""
        aria-hidden="true"
        style={{ width: 48, height: 48, display: "block" }}
      />
    }
    title="Contract.Space isn't available for your organization"
    bodyBeforeLink="Ask your IT admin to request access, or email us at "
    mailto={SUPPORT_EMAIL}
    bodyAfterLink=" to get started."
  />
);

/**
 * Shown for `no_user`, `inactive_user`, and `identity_mismatch` deny
 * statuses — the user-account resolution path (admin / support). Copy
 * uses "isn't ready" rather than "isn't added" so it stays accurate for
 * the deactivated and oid-drift edge cases too.
 */
export const BlockingNoUserScreen: React.FC = () => (
  <BlockingScreen
    icon={<PersonAdd24Regular style={{ width: 32, height: 32 }} />}
    title="You don't have access yet"
    bodyBeforeLink="Your organization uses Contract.Space, but your account isn't ready. Ask your admin to set you up, or reach us at "
    mailto={SUPPORT_EMAIL}
    bodyAfterLink="."
  />
);
